import { useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  Profile,
  THEME_FAMILIES,
  deviceTimeZone,
  THEME_MODES,
  type GlucoseUnitsPreference,
  type ThemeFamily,
  type ThemeMode,
  type UnitsPreference,
} from "@heraion/shared";
import {
  apiErrorText,
  HabitualDaysChoice,
  SettingsPillChoice,
  SettingsSelectChoice,
  TimeZoneChoice,
  WeekStartChoice,
  resolveTheme,
  useHabitualWeekdaysRead,
  useThemePreferenceContext,
  type UiTheme,
} from "@heraion/ui";
import { useThemePalette } from "./theme";
import { useShellProfile } from "./ThemeProfileBridge";
import { apiFetch } from "./api";

// Settings → Preferences on the web console: units, week start, usual days
// (#508), time zone (#510), theme.
// Mirrors mobile's Preferences sub-page (the logger inline-hints toggle is
// mobile-only and omitted here). Units is a profile scalar (get-merge-PUT via
// useProfileDoc). The language moved to Language & region (#390).

const THEME_MODE_LABELS: Record<ThemeMode, string> = {
  system: "System",
  light: "Light",
  dark: "Dark",
};

export function PreferencesScreen() {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t, i18n } = useTranslation();
  const { doc, loading, error, saveField } = useShellProfile();
  const themeCtx = useThemePreferenceContext();
  const [savingField, setSavingField] = useState<string | null>(null);
  // The failed save stays on the card that saved, after `saving` ends.
  const [saveError, setSaveError] = useState<{ field: string; text: string } | null>(null);
  const errorFor = (field: string) => (saveError?.field === field ? saveError.text : null);
  const styles = makeStyles(theme);

  // ⛔ `?? null`, NON `displayUnitOf(doc)`, e non e' una copia dimenticata: qui
  // la domanda e' «l'atleta ha scelto?», non «cosa gli mostro». Questo schermo
  // e' dove la scelta si FA, e un default a "kg" renderebbe il chip kg gia'
  // selezionato per chi non ha mai scelto — cioe' mentirebbe sullo stato.
  // Ogni schermo che invece MOSTRA un peso usa displayUnitOf.
  const units = (doc?.["unitsPreference"] as UnitsPreference | undefined) ?? null;
  const glucoseUnit =
    (doc?.["glucoseUnit"] as GlucoseUnitsPreference | undefined) ?? null;
  const preference = themeCtx?.preference;
  const weekStartDay = (doc?.["weekStartDay"] as number | undefined) ?? 1;
  const habitual = Profile.shape.habitualWeekdays.safeParse(doc?.["habitualWeekdays"]);
  const habitualRead = useHabitualWeekdaysRead(apiFetch);
  const timeZone = Profile.shape.timeZone.safeParse(doc?.["timeZone"]);
  // The browser's zone, proposed in the card and saved only on Confirm.
  const [deviceZone] = useState(deviceTimeZone);

  const save = async (field: string, value: unknown) => {
    setSavingField(field);
    setSaveError(null);
    try {
      await saveField(field, value);
    } catch (err) {
      setSaveError({ field, text: apiErrorText(err, t) });
    } finally {
      setSavingField(null);
    }
  };

  const pickTheme = (field: "family" | "mode", value: ThemeFamily | ThemeMode) => {
    if (!preference) return;
    const next = { ...preference, [field]: value };
    themeCtx?.setPreference(next);
    void save("themePreference", next);
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={theme.palette.ink2} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.flex} contentContainerStyle={styles.body}>
      <SettingsPillChoice<UnitsPreference>
        theme={theme}
        label={t("mobile.prefs.unitsLabel", { defaultValue: "Units" })}
        help={t("mobile.prefs.unitsHelp", {
          defaultValue:
            "Default weight unit for new sets. You can still override per row in the logger.",
        })}
        options={[
          { value: "kg", label: t("mobile.prefs.unitsKg", { defaultValue: "Kilograms (kg)" }) },
          { value: "lb", label: t("mobile.prefs.unitsLb", { defaultValue: "Pounds (lb)" }) },
        ]}
        value={units}
        onPick={(u) => void save("unitsPreference", u)}
        saving={savingField === "unitsPreference"}
        error={errorFor("unitsPreference") ?? error}
      />

      <SettingsPillChoice<GlucoseUnitsPreference>
        theme={theme}
        label={t("mobile.prefs.glucoseUnitsLabel", { defaultValue: "Glucose units" })}
        help={t("mobile.prefs.glucoseUnitsHelp", {
          defaultValue: "How glucose is shown and entered. Stored the same either way.",
        })}
        options={[
          { value: "mmol", label: t("mobile.prefs.glucoseMmol", { defaultValue: "mmol/L" }) },
          { value: "mgdl", label: t("mobile.prefs.glucoseMgdl", { defaultValue: "mg/dL" }) },
        ]}
        value={glucoseUnit}
        onPick={(u) => void save("glucoseUnit", u)}
        saving={savingField === "glucoseUnit"}
        error={errorFor("glucoseUnit")}
      />

      <WeekStartChoice
        theme={theme}
        value={weekStartDay}
        onPick={(d) => void save("weekStartDay", d)}
        locale={i18n.language}
        label={t("mobile.prefs.weekStartLabel", { defaultValue: "Week starts on" })}
        help={t("mobile.prefs.weekStartHelp", {
          defaultValue:
            "The first day of the week in bars and streaks.",
        })}
        saving={savingField === "weekStartDay"}
        error={errorFor("weekStartDay")}
      />

      <HabitualDaysChoice
        theme={theme}
        locale={i18n.language}
        weekStartDay={weekStartDay}
        manual={habitual.success ? habitual.data : undefined}
        read={habitualRead}
        onSave={(next) => void save("habitualWeekdays", next)}
        saving={savingField === "habitualWeekdays"}
        error={errorFor("habitualWeekdays")}
      />

      <TimeZoneChoice
        theme={theme}
        value={timeZone.success ? timeZone.data : undefined}
        onPick={(next) => void save("timeZone", next)}
        saving={savingField === "timeZone"}
        error={errorFor("timeZone")}
        deviceTimeZone={deviceZone}
      />

      <SettingsSelectChoice<ThemeFamily>
        theme={theme}
        label={t("mobile.prefs.themePaletteLabel", { defaultValue: "Theme palette" })}
        help={t("mobile.prefs.themePaletteHelp", {
          defaultValue: "Pick the colour palette used across the app.",
        })}
        options={THEME_FAMILIES.map(({ key, label }) => ({ value: key, label }))}
        value={preference?.family ?? null}
        onPick={(v) => pickTheme("family", v)}
        saving={savingField === "themePreference"}
        error={errorFor("themePreference")}
      />

      <SettingsPillChoice<ThemeMode>
        theme={theme}
        label={t("mobile.prefs.themeModeLabel", { defaultValue: "Appearance" })}
        help={t("mobile.prefs.themeModeHelp", {
          defaultValue: "Follow the system setting, or force light or dark.",
        })}
        options={THEME_MODES.map((mode) => ({
          value: mode,
          label: t(`mobile.prefs.themeMode.${mode}`, { defaultValue: THEME_MODE_LABELS[mode] }),
        }))}
        value={preference?.mode ?? null}
        onPick={(v) => pickTheme("mode", v)}
        saving={savingField === "themePreference"}
        error={errorFor("themePreference")}
      />
    </ScrollView>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s } = theme;
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: p.bg },
    body: { paddingHorizontal: s.s5, paddingVertical: s.s4, gap: s.s3 },
    center: { flex: 1, alignItems: "center", justifyContent: "center", backgroundColor: p.bg },
  });
}
